import { useEffect, useRef, useState } from 'react';

/**
 * Sprint 6 — DurationInput : input HH:MM custom avec parsing flexible.
 *
 * Pattern Float (cf. analyse/float-logtime-analysis.md §4) : on choisit
 * `type="text"` plutôt que `type="time"` pour accepter plusieurs formats
 * d'entrée (saisie pro rapide au clavier numérique).
 *
 * Formats acceptés :
 *  - `8`     → 08:00
 *  - `8h`    → 08:00
 *  - `8h30`  → 08:30
 *  - `8:30`  → 08:30
 *  - `8.5`   → 08:30
 *  - `8,5`   → 08:30
 *  - `30m`   → 00:30
 *  - `0.5`   → 00:30
 *
 * En cas d'entrée invalide : flash rouge 1.5s (data-is-invalid="true") + revert.
 *
 * Ticket #43 — commit : la valeur n'est JAMAIS persistée à la frappe. Elle l'est
 * au blur (clic ailleurs / Tab), sur Entrée, ou au démontage du champ si une
 * édition était en cours. Les flèches ↑/↓ (±1h) modifient uniquement le texte
 * affiché : elles restent utilisables pour ajuster rapidement une durée, mais
 * une frappe parasite (pavé numérique sans Verr.Num, où `8` émet ArrowUp) est
 * désormais visible à l'écran et annulable par Échap, au lieu d'être écrite en
 * base en silence.
 *
 * Props :
 *  - value      : heures décimales (number | null)
 *  - onChange   : (parsedHours: number) => void — déclenché au commit
 *  - disabled   : booléen
 *  - readOnly   : booléen (UX différente : pas d'édition, mais sélectionnable)
 *  - autoFocus  : booléen
 *  - style      : style additionnel (mergé après le style par défaut)
 *  - placeholder: chaîne (affichée si value vide)
 *  - ariaLabel  : accessibilité
 */

export function formatHHMM(hours) {
  if (hours == null || Number.isNaN(hours)) return '';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  // Cas particulier : arrondi qui fait passer à 60 → on incrémente l'heure.
  if (m === 60) return `${String(h + 1).padStart(2, '0')}:00`;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function parseDuration(text) {
  if (text == null) return null;
  const trimmed = String(text).trim().replace(',', '.');
  if (!trimmed) return null;
  let m;
  // 8h30 / 8h 30 / 8h30m / 8h
  if ((m = trimmed.match(/^(\d+(?:\.\d+)?)\s*[hH]\s*(\d{1,2})?\s*[mM]?$/))) {
    return parseFloat(m[1]) + (m[2] ? parseInt(m[2], 10) / 60 : 0);
  }
  // 8:30
  if ((m = trimmed.match(/^(\d{1,2}):(\d{2})$/))) {
    return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
  }
  // 30m
  if ((m = trimmed.match(/^(\d+(?:\.\d+)?)\s*[mM]$/))) {
    return parseFloat(m[1]) / 60;
  }
  // 8 ou 8.5
  if ((m = trimmed.match(/^(\d+(?:\.\d+)?)$/))) {
    return parseFloat(m[1]);
  }
  return null;
}

const defaultStyle = {
  width: 72,
  padding: '6px 8px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
};

export default function DurationInput({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  style,
  placeholder = '',
  ariaLabel,
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(formatHHMM(value));
  const [invalid, setInvalid] = useState(false);
  const invalidTimerRef = useRef(null);

  // Ticket #43 — miroir du state pour le commit au démontage : le cleanup ne
  // s'exécute qu'une fois (deps []) et ne verrait sinon que le premier render.
  const commitRef = useRef({ editing: false, text: '', value, onChange, readOnly, disabled });
  commitRef.current = { editing, text, value, onChange, readOnly, disabled };

  // Cleanup : timer du flash rouge + commit de l'édition en cours.
  // Sans ce commit, une saisie non validée est perdue silencieusement quand la
  // cellule est démontée (changement de semaine, rechargement, fermeture de
  // modale) — c'est le « la prise en compte ne se fait pas » du ticket #43.
  useEffect(() => {
    return () => {
      if (invalidTimerRef.current) {
        clearTimeout(invalidTimerRef.current);
        invalidTimerRef.current = null;
      }
      const c = commitRef.current;
      if (!c.editing || c.readOnly || c.disabled) return;
      if (c.text.trim() === '') return;
      const parsed = parseDuration(c.text);
      if (parsed == null || !Number.isFinite(parsed) || parsed < 0 || parsed > 24) return;
      if (parsed !== c.value) c.onChange?.(parsed);
    };
  }, []);

  // Si la valeur change depuis l'extérieur (réload semaine), resynchronise
  // l'affichage SI on n'est pas en train d'éditer.
  useEffect(() => {
    if (!editing) setText(formatHHMM(value));
  }, [value, editing]);

  const flashInvalid = () => {
    setInvalid(true);
    if (invalidTimerRef.current) clearTimeout(invalidTimerRef.current);
    invalidTimerRef.current = setTimeout(() => {
      setInvalid(false);
      invalidTimerRef.current = null;
    }, 1500);
  };

  const mergedStyle = {
    ...defaultStyle,
    ...(invalid ? { borderColor: 'var(--danger)' } : {}),
    ...(disabled || readOnly ? { opacity: 0.6, cursor: readOnly ? 'default' : 'not-allowed' } : {}),
    ...(style ?? {}),
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={editing ? text : formatHHMM(value)}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      data-is-invalid={invalid ? 'true' : 'false'}
      style={mergedStyle}
      onFocus={(e) => {
        if (readOnly || disabled) return;
        setEditing(true);
        setText(formatHHMM(value));
        // Sélection totale → permet de remplacer en tapant directement.
        // iOS ignore un `select()` synchrone au focus et laisse le curseur là
        // où l'on a touché (au milieu de « 08:00 ») : on resélectionne au tick
        // suivant, une fois le focus installé.
        const el = e.target;
        try {
          el.select();
        } catch {
          // noop
        }
        setTimeout(() => {
          try {
            if (document.activeElement === el) el.setSelectionRange(0, el.value.length);
          } catch {
            // noop
          }
        }, 0);
      }}
      onChange={(e) => {
        if (readOnly || disabled) return;
        setText(e.target.value);
      }}
      onKeyDown={(e) => {
        if (readOnly || disabled) return;
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setText(formatHHMM(value));
          // Marque comme non-éditant avant blur pour éviter onBlur de parser.
          setEditing(false);
          e.currentTarget.blur();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          // ↑ / ↓ : ajoute / retire 1h (borné à [0, 24]).
          // Ticket #43 — PAS de commit immédiat : on ne touche qu'au texte
          // affiché. La valeur est persistée comme toute autre saisie (Entrée,
          // Tab, clic ailleurs), ce qui rend le geste visible et annulable
          // (Échap) au lieu d'écrire en base sur une frappe parasite.
          e.preventDefault();
          const base = editing ? parseDuration(text) : value;
          const cur = Number.isFinite(base) ? base : 0;
          const delta = e.key === 'ArrowUp' ? 1 : -1;
          const next = Math.min(24, Math.max(0, cur + delta));
          setEditing(true);
          setText(formatHHMM(next));
        }
      }}
      onBlur={() => {
        if (readOnly || disabled) return;
        const wasEditing = editing;
        setEditing(false);
        if (!wasEditing) return;
        // Si le champ est vide, on considère cela comme "pas de saisie" :
        // on ne déclenche pas onChange, on revert simplement à la value.
        if (text.trim() === '') {
          setText(formatHHMM(value));
          return;
        }
        const parsed = parseDuration(text);
        if (parsed == null || !Number.isFinite(parsed) || parsed < 0 || parsed > 24) {
          flashInvalid();
          setText(formatHHMM(value));
          return;
        }
        if (parsed !== value) {
          onChange?.(parsed);
        } else {
          // Reformatage cosmétique (ex: "8" → "08:00").
          setText(formatHHMM(parsed));
        }
      }}
    />
  );
}
